import { useEffect, useState } from "react";

const HelpCenterDetail = ({ section, onBack }) => {
  const [activeMedia, setActiveMedia] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const showVideoThumbnail = (event, media) => {
    if (media.poster || media.thumbnail) return;

    const video = event.currentTarget;
    const previewTime = Number.isFinite(video.duration)
      ? Math.min(3, Math.max(video.duration / 3, 0.1))
      : 3;
    if (video.currentTime < 0.1) video.currentTime = previewTime;
  };

  useEffect(() => {
    if (!activeMedia) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setActiveMedia(null);
      if (activeMedia.type !== "gallery") return;
      if (event.key === "ArrowRight") {
        setActiveImageIndex((current) =>
          (current + 1) % activeMedia.images.length
        );
      }
      if (event.key === "ArrowLeft") {
        setActiveImageIndex((current) =>
          (current - 1 + activeMedia.images.length) % activeMedia.images.length
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeMedia]);

  if (!section) return <div>Not found</div>;

  const visibleContent = section.content || [];

  const firstExampleIndex = visibleContent.findIndex(
    (block) => block.type === "example"
  );

  const openMedia = (media) => {
    setActiveImageIndex(0);
    setActiveMedia(media);
  };

  const mediaPlaceholder = (label, large = false) => (
    <span className={`flex w-full items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 font-semibold text-gray-500 ${
      large ? "h-[min(70vh,36rem)] rounded-lg text-lg" : "h-full text-sm"
    }`}>
      {label} placeholder
    </span>
  );

  const renderMedia = (media, mediaIndex, thumbnail = false) => (
    <figure key={mediaIndex} className="mx-auto w-full max-w-56">
      <button
        type="button"
        onClick={() => openMedia(media)}
        className={`group relative mx-auto block w-full max-w-56 overflow-hidden rounded-lg border border-gray-200 bg-gray-100 ${
          thumbnail ? "h-28" : ""
        }`}
        aria-label={`Open ${media.type} in a larger view`}
      >
        {media.type === "image" && media.placeholder &&
          mediaPlaceholder("Image")}
        {media.type === "image" && !media.placeholder && (
          <img
            src={media.src}
            alt={media.alt || ""}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        )}
        {media.type === "video" && media.placeholder && (
          <>
            {mediaPlaceholder("Video")}
            <span className="absolute inset-0 flex items-center justify-center text-3xl text-gray-600">
              ▶
            </span>
          </>
        )}
        {media.type === "video" && !media.placeholder && (
          <>
            <video
              className="h-full w-full object-cover"
              preload="auto"
              muted
              playsInline
              poster={media.poster || media.thumbnail || undefined}
              onLoadedMetadata={(event) => showVideoThumbnail(event, media)}
            >
              <source src={media.src} type={media.mimeType || "video/mp4"} />
            </video>
            <span className="absolute inset-0 flex items-center justify-center bg-black/15 text-3xl text-white transition-colors group-hover:bg-black/25">
              ▶
            </span>
          </>
        )}
      </button>
      {media.caption && !thumbnail && (
        <figcaption className="mt-2 text-sm text-gray-600">
          {media.caption}
        </figcaption>
      )}
    </figure>
  );

  const renderMediaGroup = (media = []) => {
    const images = media.filter(
      (item) => item.type === "image" && (item.src || item.placeholder)
    );
    const otherMedia = media.filter(
      (item) => item.type !== "image" && (item.src || item.placeholder)
    );
    const displayMedia = images.length > 1
      ? [{
          type: "gallery",
          images,
          caption: images.find((image) => image.caption)?.caption,
        }, ...otherMedia]
      : [...images, ...otherMedia];

    return displayMedia.map((item, mediaIndex) => {
      if (item.type !== "gallery") return renderMedia(item, mediaIndex, true);

      return (
        <figure key={`gallery-${mediaIndex}`} className="mx-auto w-full max-w-56">
          <button
            type="button"
            onClick={() => openMedia(item)}
            className="group relative mx-auto block h-28 w-[calc(100%-0.75rem)]"
            aria-label={`Open gallery of ${item.images.length} images`}
          >
            <span className="absolute inset-0 translate-x-3 -rotate-2 rounded-lg border border-gray-200 bg-gray-200" />
            <span className="absolute inset-0 translate-x-1.5 rotate-1 rounded-lg border border-gray-200 bg-gray-100" />
            <span className="absolute inset-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              {item.images[0].placeholder ? (
                mediaPlaceholder("Images")
              ) : (
                <img
                  src={item.images[0].src}
                  alt={item.images[0].alt || ""}
                  className="h-full w-full object-cover transition-transform group-hover:scale-105"
                />
              )}
              <span className="absolute bottom-2 right-2 rounded-full bg-black/70 px-2.5 py-1 text-xs font-semibold text-white">
                1 / {item.images.length}
              </span>
            </span>
          </button>
        </figure>
      );
    });
  };

  return (
    <div className="mx-auto min-h-screen max-w-6xl px-12 pt-12">
      <button
        onClick={onBack}
        className="mb-6 text-blue-600 hover:underline"
        >
        ← Back
        </button>  

      <h1 className="text-3xl font-bold mb-6">{section.title}</h1>

      {/* VIDEO */}
      {section.video && (
        <div className="mb-6 flex justify-center">
            <video
            className="w-full max-w-2xl rounded-lg"
            controls
            preload="metadata"
            >
            <source src={section.video} type="video/mp4" />
            Your browser does not support the video tag.
            </video>
        </div>
      )}

      {/* CONTENT */}
      <div>
        {visibleContent.map((block, index) => {
          switch (block.type) {
            case "header":
              return (
                <h2 key={index} className="mb-4 text-xl font-semibold">
                  {block.text}
                </h2>
              );
            case "text":
              return <p key={index} className="mb-4 whitespace-pre-line">
                      {block.text}
                     </p>;
            case "image":
              return (
                <img
                  key={index}
                  src={block.src}
                  alt=""
                  className="mb-4 rounded-lg"
                />
              );
            case "example":
              return (
                <div key={index}>
                  {index === firstExampleIndex && (
                    <div className="hidden grid-cols-[1.05fr_1.1fr_1.3fr_1.55fr] rounded-t-xl bg-gradient-to-r from-blue-500 to-blue-400 text-center text-sm font-semibold text-white lg:grid">
                      <div className="flex items-center justify-center p-4">Developmental need addressed</div>
                      <div className="flex items-center justify-center border-l border-white/20 p-4">Rapport or engagement activity</div>
                      <div className="flex items-center justify-center border-l border-white/20 p-4">Example clinician language</div>
                      <div className="flex items-center justify-center border-l border-white/20 p-4">Plutonic feature in practice</div>
                    </div>
                  )}
                  <article className="grid overflow-hidden border border-t-0 border-gray-200 bg-white first:border-t lg:grid-cols-[1.05fr_1.1fr_1.3fr_1.55fr]">
                    <div className="p-5">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 lg:hidden">Developmental need addressed</p>
                      <p className="font-semibold">{block.need}</p>
                    </div>
                    <div className="border-t border-gray-200 p-5 lg:border-l lg:border-t-0">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 lg:hidden">Rapport or engagement activity</p>
                      <p>{block.activity}</p>
                    </div>
                    <div className="border-t border-gray-200 p-5 lg:border-l lg:border-t-0">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 lg:hidden">Example clinician language</p>
                      <p className="italic text-gray-800">{block.clinicianLanguage}</p>
                    </div>
                    <div className="space-y-4 border-t border-gray-200 p-5 lg:border-l lg:border-t-0">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-blue-600 lg:hidden">Plutonic feature in practice</p>
                      {(!block.media || block.media.length === 0) && (
                        <p>{block.featureInPractice}</p>
                      )}
                      {block.media?.length > 0 && (
                        <div className="flex w-full flex-wrap justify-center gap-3">
                          {renderMediaGroup(block.media)}
                        </div>
                      )}
                    </div>
                  </article>
                </div>
              );
            default:
              return null;
          }
        })}
      </div>

      {activeMedia && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4 sm:p-8"
          role="dialog"
          aria-modal="true"
          aria-label="Media preview"
          onClick={() => setActiveMedia(null)}
        >
          <div
            className="relative w-full max-w-5xl rounded-xl bg-white p-4 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActiveMedia(null)}
              className="absolute -right-3 -top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-2xl text-gray-700 shadow-lg"
              aria-label="Close media preview"
            >
              ×
            </button>
            {activeMedia.type === "gallery" ? (
              <div className="relative">
                {activeMedia.images[activeImageIndex].placeholder ? (
                  mediaPlaceholder(`Image ${activeImageIndex + 1}`, true)
                ) : (
                  <img
                    src={activeMedia.images[activeImageIndex].src}
                    alt={activeMedia.images[activeImageIndex].alt || ""}
                    className="max-h-[80vh] w-full rounded-lg object-contain"
                  />
                )}
                <button
                  type="button"
                  onClick={() => setActiveImageIndex((current) =>
                    (current - 1 + activeMedia.images.length) % activeMedia.images.length
                  )}
                  className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/65 text-2xl text-white shadow-lg hover:bg-black/80"
                  aria-label="Previous image"
                >
                  &lsaquo;
                </button>
                <button
                  type="button"
                  onClick={() => setActiveImageIndex((current) =>
                    (current + 1) % activeMedia.images.length
                  )}
                  className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-black/65 text-2xl text-white shadow-lg hover:bg-black/80"
                  aria-label="Next image"
                >
                  &rsaquo;
                </button>
                <span className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-3 py-1 text-sm font-medium text-white">
                  {activeImageIndex + 1} / {activeMedia.images.length}
                </span>
              </div>
            ) : activeMedia.placeholder ? (
              mediaPlaceholder(
                activeMedia.type === "video" ? "Video" : "Image",
                true
              )
            ) : activeMedia.type === "image" ? (
              <img
                src={activeMedia.src}
                alt={activeMedia.alt || ""}
                className="max-h-[80vh] w-full rounded-lg object-contain"
              />
            ) : (
              <video
                className="max-h-[80vh] w-full rounded-lg bg-black"
                controls
                autoPlay
              >
                <source
                  src={activeMedia.src}
                  type={activeMedia.mimeType || "video/mp4"}
                />
                Your browser does not support the video tag.
              </video>
            )}
            {(activeMedia.type === "gallery"
              ? activeMedia.images[activeImageIndex].caption
              : activeMedia.caption) && (
              <p className="mt-3 text-sm text-gray-600">
                {activeMedia.type === "gallery"
                  ? activeMedia.images[activeImageIndex].caption
                  : activeMedia.caption}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpCenterDetail;
